import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api, fmtUSD } from "../lib/api";
import CONTENT from "../lib/serviceContent";

const SVC_META = {
  "brand-scan": {
    en: { t: "Brand Scan — Strategic diagnosis · $700", d: "Fast diagnosis of an existing brand: scorecard + 30-day action plan. Delivered in 5 business days. Fixed price $700." },
    es: { t: "Brand Scan — Diagnóstico estratégico · $700", d: "Diagnóstico rápido de una marca existente: scorecard + plan de acción a 30 días. En 5 días hábiles. Precio fijo $700." },
  },
  "brand-development": {
    en: { t: "Brand Development — For new brands · $1,900", d: "Positioning, concept, key messages, brand design, final logo design + export package, core brand system (typography, colour, usage direction) and 30-day launch plan. 10 business days. Fixed price $1,900." },
    es: { t: "Brand Development — Marcas nuevas · $1,900", d: "Posicionamiento, concepto, mensajes clave, diseño de marca, diseño de logo final + paquete de export, sistema de marca base (tipografía, color, dirección de uso) y plan de lanzamiento a 30 días. 10 días hábiles. Precio fijo $1,900." },
  },
  "brand-strategy": {
    en: { t: "Brand Strategy & Narrative Universe · $1,900", d: "Story, tone, cultural world and campaign angles for existing brands. 10 business days. Fixed price $1,900." },
    es: { t: "Estrategia y Universo Narrativo · $1,900", d: "Historia, tono, mundo cultural y ángulos de campaña para marcas existentes. 10 días hábiles. Precio fijo $1,900." },
  },
  "brand-partner": {
    en: { t: "Brand Partner — Ongoing direction · $2,900/mo", d: "Async brand direction: 4 strategic requests per month, 2–3 day response, monthly roadmap, visual universe direction." },
    es: { t: "Brand Partner — Dirección continua · $2,900/mes", d: "Dirección de marca async: 4 requests estratégicos al mes, respuesta 2–3 días, roadmap mensual y dirección de universo visual." },
  },
  "brand-office": {
    en: { t: "Brand Office — Application-based advisory", d: "Direct strategic access, 1 call per week, founder advisory. Application-based, minimum 3 months." },
    es: { t: "Brand Office — Asesoría por aplicación", d: "Acceso estratégico directo, 1 call por semana, asesoría de fundador. Por aplicación, mínimo 3 meses." },
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);

  const meta = (SVC_META[slug] && SVC_META[slug][lang]) || null;
  useSeo({
    title: meta ? `${meta.t} · Brand and Behavior` : (lang === "es" ? "Servicio · Brand and Behavior" : "Service · Brand and Behavior"),
    description: meta ? meta.d : undefined,
    path: `${lang === "es" ? "/es" : ""}/services/${slug}`,
  });

  useEffect(() => {
    let alive = true;
    api.get(`/services/${slug}?lang=${lang}`)
      .then((r) => { if (alive) { setSvc(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setErr("not_found"); setLoading(false); } });
    return () => { alive = false; };
  }, [slug, lang]);

  const start = async () => {
    if (!svc) return;
    if (svc.billing === "application") { navigate("/apply"); return; }
    const accepted = window.confirm(
      t(
        "Before paying, please confirm:\n\n• You accept the Terms, Scope and Refund Policy.\n• You understand Brand & Behavior provides strategic direction, not legal/trademark/compliance advice.\n• Timelines begin only after complete intake.\n\nContinue to secure payment?",
        "Antes de pagar, confirma:\n\n• Aceptas los Términos, Alcance y Política de Reembolsos.\n• Entiendes que Brand & Behavior ofrece dirección estratégica, no asesoría legal/marca/cumplimiento.\n• Los plazos comienzan tras intake completo.\n\n¿Continuar al pago seguro?"
      )
    );
    if (!accepted) return;
    setStarting(true);
    try {
      const { data } = await api.post("/checkout/start", { slug: svc.slug, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      alert(t("Could not start checkout. Try again.", "No se pudo iniciar el checkout. Inténtalo de nuevo."));
      setStarting(false);
    }
  };

  if (loading) return <main className="app-main"><section className="page-hero"><p>{t("Loading…", "Cargando…")}</p></section></main>;
  if (err || !svc) return <main className="app-main"><section className="page-hero"><h1>404</h1><p>{t("Service not found.", "Servicio no encontrado.")} <Link to="/services">{t("Back to services", "Volver a servicios")}</Link></p></section></main>;

  const c = CONTENT[svc.slug] || {};
  const lead = c.lead?.[lang] || c.lead?.en || svc.tagline;
  const includes = c.includes?.[lang] || c.includes?.en || [];
  const notIncluded = c.notIncluded?.[lang] || c.notIncluded?.en || [];
  const rules = c.rules?.[lang] || c.rules?.en || "";

  return (
    <main className="app-main svc-detail" data-testid={`service-${svc.slug}`}>
      <section className="page-hero">
        <div className="kicker">
          <Link to="/services" style={{ color: "var(--muted)" }}>{t("Services", "Servicios")}</Link> · {svc.format}
        </div>
        <h1>{svc.name}</h1>
        <p>{lead}</p>
      </section>

      <div className="price-row">
        <div className="price-big">
          {svc.price ? fmtUSD(svc.price) : <em className="it">{t("On request", "Bajo consulta")}</em>}
          {svc.billing === "monthly" && <span style={{ fontFamily: "Inter, sans-serif", fontStyle: "normal", fontSize: 14, letterSpacing: ".06em", color: "var(--muted)", marginLeft: 8 }}>/{t("month", "mes")}</span>}
        </div>
        <div className="meta">· {svc.duration}</div>
        {svc.adjustments != null && svc.adjustments > 0 && (
          <div className="meta">· {svc.adjustments} {t("adjustment rounds", "rondas de ajuste")}</div>
        )}
        {svc.monthly_requests && (
          <div className="meta">· {svc.monthly_requests} {t("requests / month", "requests / mes")}</div>
        )}
      </div>

      <div className="start-row">
        <button className="button" onClick={start} disabled={starting} data-testid={`start-${svc.slug}`}>
          {svc.billing === "application"
            ? t("Apply now", "Aplicar ahora")
            : starting
            ? t("Redirecting…", "Redirigiendo…")
            : svc.billing === "monthly"
            ? t(`Start Brand Partner — ${fmtUSD(svc.price)}/mo`, `Iniciar Brand Partner — ${fmtUSD(svc.price)}/mes`)
            : t(`Pay ${fmtUSD(svc.price)} and start`, `Pagar ${fmtUSD(svc.price)} e iniciar`)}
        </button>
      </div>

      <h2 className="section-h">{t("What's included", "Qué está incluido")}</h2>
      <ul className="checks">
        {includes.map((it) => <li key={it}>{it}</li>)}
      </ul>

      <h2 className="section-h">{t("Not included", "No incluido")}</h2>
      <ul className="checks not">
        {notIncluded.map((it) => <li key={it}>{it}</li>)}
      </ul>

      <h2 className="section-h">{t("Rules & format", "Reglas y formato")}</h2>
      <p style={{ fontSize: 16, lineHeight: 1.5, color: "var(--muted)", maxWidth: 780 }}>{rules}</p>

      <div style={{ height: 80 }} />
    </main>
  );
}
